import { memo, forwardRef, useMemo } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import type { LinkProps } from 'react-router';
import { Link as RRLink } from 'react-router';

import Tooltip from '@mui/material/Tooltip';

import useI18n from '@/hooks/useI18n.ts';
import normalizePath from '@/hooks/useRouter/normalizePath.ts';
import { isProtocolUrl } from '@/utils/string';
import { getCurrentURI } from '@/hooks/useRouter/useRouter';

/**
 * @desc this interface is to support this use case
 * ```
 *  <Button<LinkElement> component={Link} slotProps={root: { ... LinkProps ...}} ...other Button props...>
 * ```
 */
export interface LinkElement extends Element {
  includeBackToCurrentUri?: boolean;
  state?: LinkProps['state'];
}

export interface ILinkProps extends HTMLAttributes<HTMLAnchorElement> {
  href: string;
  target?: string;
  children: ReactNode;
  state?: LinkProps['state'];
  includeBackToCurrentUri?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

const Link = forwardRef<HTMLAnchorElement, ILinkProps>(
  (
    {
      href,
      includeBackToCurrentUri,
      children,
      disabled,
      disabledTooltip,
      ...rest
    }: ILinkProps,
    ref
  ) => {
    const { locale } = useI18n();

    const to = isProtocolUrl(href) ? href : `/${locale}/${normalizePath(href)}`;

    const state = useMemo(() => {
      const returnState: { backUri?: string } = {};

      if (includeBackToCurrentUri) {
        returnState.backUri = getCurrentURI(true).replace(`/${locale}`, '');
      }

      return returnState;
    }, [includeBackToCurrentUri, locale]);

    if (disabled) {
      const render = <a {...rest}>{children}</a>;
      if (disabledTooltip) {
        return (
          <Tooltip title={disabledTooltip} placement="top">
            {render}
          </Tooltip>
        );
      }
      return render;
    }

    return (
      <RRLink
        {...rest}
        to={to}
        ref={ref}
        state={state}
        viewTransition={!import.meta.env.STORYBOOK}
      >
        {children}
      </RRLink>
    );
  }
);

Link.displayName = 'Link';

const LinkMemo = memo(Link);
LinkMemo.displayName = 'Link';

export default LinkMemo;
